import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const SlackIcon = () => (
  <img src="https://auth.openorg.fyi/storage/v1/object/public/web-assets//slack-new-logo.svg" alt="Slack logo" className="inline h-5 w-5 ml-1 align-text-bottom" />
);

export default function Stage1() {
  return (
    <div className="space-y-8">
      <PlaybookSection
        title="Stage 1: Own It & Address It"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="Acknowledge the issue and assess its impact"
      >
        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">TL;DR</h2>
          <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
            <p className="mb-4">You need to do 2 things:</p>
            <ul className="list-none space-y-3">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">1.</span>
                <span><strong>Address the Mess</strong>: Craft an honest, straightforward message acknowledging the issue.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">2.</span>
                <span><strong>Assess the Impact</strong>: Rapidly determine who's affected and to what extent.</span>
              </li>
            </ul>
            <p className="mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Triggers For Action</h2>
          <ExpandableCallout type="info" title="What triggers this playbook…" defaultOpen={true}>
            <p>Basically any operational or cultural f*ck up. Examples:</p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Payroll issue – lots of folks didn't receive their wages</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Spent a ton of time and money hiring an executive who turned out to be incompetent / ineffective</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Made a poor decision that lost the company a lot of money/trust</span>
              </li>
            </ul>
          </ExpandableCallout>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Action: Communicate Quickly</h2>
          <p className="mb-4">Send out an honest update about the situation that clearly expresses what the issue is, the immediate actions being taken and a commitment to keep folks in the loop. <SlackIcon /></p>
          <p className="mb-2 font-semibold">Example Template:</p>
          <CopyToNotion title="Comms Template: Addressing Our Recent Oversight" slack>
            <pre className="bg-gray-900 rounded p-4 text-sm overflow-x-auto mb-4">
{`Subject: Addressing Our Recent Oversight

Team,

I want to bring to your attention a recent issue. 
[brief description of the issue]. 

We recognize the implications of this and are committed to handling it 
with dilligence and transparency.

Here's what we know & what we're doing in the immediate term:
- [Immediate actions being taken]
- [Any changes to prevent similar issues]

Right now you know what we know, we'll continue to update you as best we can.

Best,
[Your Name]`}
            </pre>
          </CopyToNotion>
        </PlaybookCard>

        <PlaybookCard>
          <h2 className="text-xl font-semibold text-white mb-4">Action: Gather Data And Assess Impact</h2>
          <ul className="space-y-2 mb-4">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Use tools like surveys and direct feedback (1-2-1s / people manager team talks) to understand the breadth and depth of the issue.</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Understand if risk or issue and whether that is company wide or departmental focused:</span>
            </li>
            <ul className="pl-6 space-y-1 mt-1">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Is this a company wide {'{retention}'} threat?</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Is this a specific department's {'{disengagement}'} issue?</span>
              </li>
            </ul>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>5 whys root cause analysis can be useful for this:</span>
            </li>
            <div className="mt-2 mb-2">
              <ToolCallout
                tool="miro"
                label="5 Whys Root Cause Analysis Template"
                href="https://miro.com/templates/5-whys/"
                description="A Miro template for running a 5 Whys root cause analysis."
              />
            </div>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Put together an action plan with clear horizons and a primary goal for each:</span>
            </li>
            <ul className="pl-6 space-y-1 mt-1">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>This Week</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>This Month</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <span>Longer term</span>
              </li>
            </ul>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Keep the team in the loop and frame as 🌱Learning.</span>
            </li>
          </ul>
          <p className="mb-2 font-semibold">Example Comms Template</p>
          <CopyToNotion title="Comms Template: Next Steps on Our Recent Learning">
            <pre className="bg-gray-900 rounded p-4 text-sm overflow-x-auto mb-4">
{`Subject: Next Steps on Our Recent Learning

Team,

Following our recent challenge with [specific issue], 
here's what we've learned and how we're moving forward:

- [Key findings from the analysis/impact assessment]
- [Specific actions planned to amend or improve the situation {link to draft}

This experience, although difficult, is a stepping stone towards 
improvement.  

Thank you for your feedback, please comment on the attached doc any 
improvements/ideas.

Best,
[Your Name]
`}
            </pre>
          </CopyToNotion>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
} 